import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Bot } from "lucide-react";
import { adminGetSettings, adminPutSettings } from "@/lib/api";
import { adminInputCls, adminGoldBtnCls } from "./shared";

export const AiTab = ({ onAuthError }: { onAuthError: () => void }) => {
  const [settings, setSettings] = useState<any>(null);
  const [instructions, setInstructions] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetSettings()
      .then((r) => {
        setSettings(r.data);
        setInstructions(r.data.ai_instructions || "");
      })
      .catch((e) => {
        if (e?.response?.status === 401) onAuthError();
        else setSettings({});
      });
  }, [onAuthError]);

  const save = async () => {
    setSaving(true);
    try {
      await adminPutSettings({ ai_instructions: instructions });
      toast.success("AI assistant instructions saved");
    } catch (e: any) {
      if (e?.response?.status === 401) onAuthError();
      else toast.error("Save failed");
    }
    setSaving(false);
  };

  if (!settings) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  const biz = settings.business || {};
  const areas = (settings.service_areas || []).map((a: any) => a.name).join(", ");

  const infoRow = (label: string, value: string) => (
    <div className="border-b border-white/10 py-3 last:border-b-0">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">{label}</p>
      <p className="mt-1 text-sm leading-relaxed text-neutral-200">{value || "—"}</p>
    </div>
  );

  return (
    <div data-testid="admin-ai-tab">
      <div className="flex items-start gap-3 border border-white/10 bg-charcoal p-6">
        <Bot className="mt-1 h-6 w-6 shrink-0 text-gold" strokeWidth={1.5} />
        <div>
          <p className="font-display text-lg font-black uppercase tracking-tight text-white">
            What Ask R3 knows
          </p>
          <p className="mt-2 text-sm leading-relaxed text-neutral-400">
            The assistant answers only from the information below — pulled live from your website
            settings on every question. Update it in the Business Information and Service Areas
            tabs; no code changes needed. It never invents pricing, availability, licenses,
            insurance, timelines, or guarantees.
          </p>
        </div>
      </div>

      <div className="mt-6 border border-white/10 bg-charcoal p-6" data-testid="ai-knowledge-summary">
        {infoRow("Contact", `${biz.contact_name} · ${biz.phone} · ${biz.email} · R3 Services also ${biz.r3_phone2}`)}
        {infoRow("Drywall services", (biz.services_drywall || []).join(", "))}
        {infoRow("R3 Services", (biz.services_r3 || []).join(", "))}
        {infoRow("Service areas", areas)}
        {infoRow("FAQ answers", "Builders/GCs, new construction, drywall services, commercial, R3 Services list, Spanish, how to request a bid (from the website FAQ)")}
        {infoRow("Bid instructions", "Direct visitors to Send Plans / Request a Bid at /request-a-bid")}
        {infoRow("Review instructions", "Direct visitors to the Reviews page at /reviews; never writes reviews for customers")}
      </div>

      <div className="mt-6 border border-white/10 bg-charcoal p-6">
        <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
          Extra instructions for the assistant (optional)
        </label>
        <textarea
          data-testid="ai-instructions-input"
          rows={4}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="e.g. During December, mention that scheduling may be limited. Never add pricing, promises, or claims here."
          className={`${adminInputCls} resize-none`}
        />
        <button data-testid="ai-save-btn" onClick={save} disabled={saving} className={`${adminGoldBtnCls} mt-4`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Instructions
        </button>
      </div>
    </div>
  );
};
